import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppShell } from "@/components/AppShell";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatGBP, formatGBPExact, STATUS_STYLES, BRAND } from "@/lib/avocado";
import { StatusBadge, StatusPipeline } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Edit3, Search, Trash2, AlertTriangle, KeyRound, Copy, Check, MessageCircle, RefreshCw } from "lucide-react";

const STAGES = ["Referred", "Confirmed Client", "Sale Completed"];

function RemoveDialog({ referral, open, onClose, onRemoved }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  if (!referral) return null;
  const heading = referral.property_address || referral.client_name || "Referral";
  const doRemove = async () => {
    if (!reason.trim()) { toast.error("Please enter a reason so the referrer knows why."); return; }
    setBusy(true);
    try {
      const { data } = await api.delete(`/admin/referrals/${referral.id}`, { data: { reason: reason.trim() } });
      toast.success("Referral removed. The referrer will see the reason.");
      onRemoved?.(data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not remove referral");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="admin-remove-dialog">
        <DialogHeader>
          <div className="flex items-center gap-2"><AlertTriangle size={20} color="#B91C1C" /><p className="uppercase-label" style={{ color: "#B91C1C" }}>Remove referral</p></div>
          <DialogTitle className="font-display text-2xl">{heading}</DialogTitle>
          <DialogDescription>
            The referral will be marked as <strong>not proceeding</strong>. {referral.referrer_name} will see this in their dashboard along with the reason below.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5 mt-2">
          <Label className="uppercase-label">Reason (shown to referrer)</Label>
          <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Client decided not to proceed with Avocado at this time"
            data-testid="admin-remove-reason-input" />
        </div>
        <DialogFooter className="mt-3">
          <Button variant="ghost" onClick={onClose} className="rounded-full" data-testid="admin-remove-cancel">Cancel</Button>
          <Button onClick={doRemove} disabled={busy} className="rounded-full font-bold" style={{ background: "#B91C1C", color: "white" }} data-testid="admin-remove-confirm">
            {busy ? <Loader2 className="animate-spin" size={16} /> : "Remove referral"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function KpiCard({ label, value, sub, testId }) {
  return (
    <div className="card-lift bg-white border border-border/60 rounded-xl p-6" data-testid={testId}>
      <div className="uppercase-label">{label}</div>
      <div className="font-display text-3xl md:text-4xl mt-2 tabular" style={{ color: "#1C2419" }}>{value}</div>
      {sub && <div className="text-xs mt-1.5" style={{ color: "#5C6B57" }}>{sub}</div>}
    </div>
  );
}

function EditReferralDialog({ referral, open, onClose, onSaved }) {
  const [status, setStatus] = useState(referral?.status || "Referred");
  const [salePrice, setSalePrice] = useState(referral?.sale_price ?? "");
  const [commission, setCommission] = useState(referral?.commission ?? "");
  const [clientName, setClientName] = useState(referral?.client_name || "");
  const [clientPhone, setClientPhone] = useState(referral?.client_phone || "");
  const [clientEmail, setClientEmail] = useState(referral?.client_email || "");
  const [bank, setBank] = useState(null);
  const [busy, setBusy] = useState(false);
  const [resetLink, setResetLink] = useState(null);
  const [resetBusy, setResetBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const generateResetLink = async () => {
    setResetBusy(true);
    try {
      const { data } = await api.post(`/admin/referrers/${referral.referrer_id}/reset-link`);
      setResetLink(data);
      setCopied(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not generate link");
    } finally { setResetBusy(false); }
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(resetLink.reset_url); setCopied(true); toast.success("Link copied — paste into WhatsApp / text"); }
    catch { toast.error("Copy failed — select and copy manually"); }
  };

  const whatsAppMessage = () => {
    if (!resetLink) return "";
    const first = (resetLink.referrer_name || "there").split(" ")[0];
    return `Hi ${first} 👋 here's a link to reset your Avocado Property referrer password: ${resetLink.reset_url}\n\nGood for 24 hours. Any bother, text me on 07498 865031.\n\nTom`;
  };

  const openWhatsApp = () => {
    const url = `https://wa.me/?text=${encodeURIComponent(whatsAppMessage())}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const copyMessage = async () => {
    try { await navigator.clipboard.writeText(whatsAppMessage()); toast.success("Full message copied — paste anywhere"); }
    catch { toast.error("Copy failed"); }
  };

  useEffect(() => {
    if (referral) {
      setStatus(referral.status);
      setSalePrice(referral.sale_price ?? "");
      setCommission(referral.commission ?? "");
      setClientName(referral.client_name || "");
      setClientPhone(referral.client_phone || "");
      setClientEmail(referral.client_email || "");
      setBank(null);
      api.get(`/admin/referrers/${referral.referrer_id}/bank`).then(({ data }) => setBank(data)).catch(() => setBank(null));
    }
  }, [referral]);

  if (!referral) return null;

  const previewFee = commission ? (parseFloat(commission) * 0.05).toFixed(2) : "0.00";
  const pipelineIndex = STAGES.indexOf(status);
  const canEditClient = pipelineIndex >= 1; // Confirmed Client or beyond
  const heading = referral.property_address || referral.client_name || "Referral";

  const save = async () => {
    setBusy(true);
    try {
      const payload = { status };
      if (salePrice !== "" && salePrice !== null) payload.sale_price = parseFloat(salePrice);
      if (commission !== "" && commission !== null) payload.commission = parseFloat(commission);
      if (canEditClient) {
        if (clientName !== (referral.client_name || "")) payload.client_name = clientName.trim();
        if (clientPhone !== (referral.client_phone || "")) payload.client_phone = clientPhone.trim();
        if (clientEmail !== (referral.client_email || "")) payload.client_email = clientEmail.trim();
      }
      const { data } = await api.patch(`/admin/referrals/${referral.id}`, payload);
      toast.success("Referral updated");
      onSaved?.(data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="admin-edit-dialog">
        <DialogHeader>
          <p className="uppercase-label">Admin edit · {referral.referrer_name} {referral.referrer_code ? `· ${referral.referrer_code}` : ""}</p>
          <DialogTitle className="font-display text-3xl">{heading}</DialogTitle>
          <DialogDescription>{referral.client_name ? `Client on file: ${referral.client_name}` : "No client details on file yet — GDPR-safe until a valuation is booked."}</DialogDescription>
        </DialogHeader>

        <div className="mt-2"><StatusPipeline current={status} /></div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
          <div className="md:col-span-1 space-y-1.5">
            <Label className="uppercase-label">Stage</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="admin-status-select" className="h-11"><SelectValue /></SelectTrigger>
              <SelectContent>
                {STAGES.map((s) => (<SelectItem key={s} value={s} data-testid={`admin-status-option-${s}`}>{s}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="uppercase-label">Sale price (£) · admin only</Label>
            <Input type="number" min="0" step="100" value={salePrice} onChange={(e) => setSalePrice(e.target.value)}
              placeholder="e.g. 525000" className="h-11" data-testid="admin-sale-price-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="uppercase-label">Tom's commission (£)</Label>
            <Input type="number" min="0" step="50" value={commission} onChange={(e) => setCommission(e.target.value)}
              placeholder="e.g. 7875" className="h-11" data-testid="admin-commission-input" />
          </div>
        </div>

        {/* GDPR — client details unlocked once Confirmed Client */}
        <div className="mt-4 rounded-xl p-4 border-2" style={{ background: canEditClient ? "#F7FAEF" : "#FAF8F3", borderColor: canEditClient ? "#DFF1B7" : "#E5E7E1" }} data-testid="admin-client-panel">
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="uppercase-label" style={{ color: canEditClient ? "#4C6812" : "#7A6D5F" }}>
              Client details {canEditClient ? "(editable)" : "(locked until Confirmed Client)"}
            </div>
            {!canEditClient && <span className="text-[10px] tabular" style={{ color: "#7A6D5F" }}>GDPR</span>}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label className="uppercase-label">Client name</Label>
              <Input value={clientName} onChange={(e) => setClientName(e.target.value)} disabled={!canEditClient}
                placeholder={canEditClient ? "Sarah & James Whitfield" : "Set stage → Confirmed Client to unlock"}
                className="h-11" data-testid="admin-client-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="uppercase-label">Phone</Label>
              <Input value={clientPhone} onChange={(e) => setClientPhone(e.target.value)} disabled={!canEditClient}
                placeholder="07…" className="h-11" data-testid="admin-client-phone-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="uppercase-label">Email</Label>
              <Input type="email" value={clientEmail} onChange={(e) => setClientEmail(e.target.value)} disabled={!canEditClient}
                placeholder="optional" className="h-11" data-testid="admin-client-email-input" />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3 mt-4">
          <div className="rounded-lg p-4 border" style={{ background: "#F9F9F6", borderColor: "#E5E7E1" }}>
            <div className="uppercase-label">Sale price</div>
            <div className="font-display text-xl tabular mt-1">{salePrice ? formatGBP(salePrice) : "—"}</div>
          </div>
          <div className="rounded-lg p-4 border" style={{ background: "#F9F9F6", borderColor: "#E5E7E1" }}>
            <div className="uppercase-label">Commission</div>
            <div className="font-display text-xl tabular mt-1">{commission ? formatGBP(commission) : "—"}</div>
          </div>
          <div className="rounded-xl p-4 border-2" style={{ background: "#FFF0F8", borderColor: "#FFAED8" }}>
            <div className="uppercase-label" style={{ color: BRAND.pink }}>Referrer 5% fee</div>
            <div className="font-display text-xl tabular mt-1 font-bold" style={{ color: BRAND.pink }} data-testid="admin-preview-fee">{formatGBPExact(parseFloat(previewFee))}</div>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-3">
          <Button variant="ghost" onClick={onClose} className="rounded-full" data-testid="admin-cancel-button">Cancel</Button>
          <Button onClick={save} disabled={busy} className="btn-brand rounded-full font-bold" data-testid="admin-save-button">
            {busy ? <Loader2 className="animate-spin" size={16} /> : "Save changes"}
          </Button>
        </div>

        {bank && (
          <div className="mt-4 rounded-xl p-4 border-2" style={{ background: "#FFFCF7", borderColor: "#F0E5D5" }} data-testid="admin-bank-panel">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="uppercase-label" style={{ color: BRAND.pink }}>Payout — bank details on file</div>
              <Button size="sm" variant="ghost" onClick={generateResetLink} disabled={resetBusy}
                className="rounded-full text-xs gap-1 h-8" style={{ color: BRAND.pink }} data-testid="admin-gen-reset-link">
                {resetBusy ? <Loader2 className="animate-spin" size={12} /> : <><KeyRound size={12} /> Send reset link</>}
              </Button>
            </div>
            {bank.has_details ? (
              <div className="mt-2 text-sm">
                <div><strong>{bank.account_name}</strong></div>
                <div className="tabular" style={{ color: "#4B4B4B" }}>Sort code: {bank.sort_code} · Account: {bank.account_number}</div>
                <div className="text-xs mt-1" style={{ color: "#7A6D5F" }}>Updated {bank.updated_at ? new Date(bank.updated_at).toLocaleDateString("en-GB") : "—"}</div>
              </div>
            ) : (
              <div className="mt-2 text-sm italic" style={{ color: "#B45309" }}>
                {bank.referrer_name || "Referrer"} hasn't added bank details yet — they'll need to before you can pay out.
              </div>
            )}
            {resetLink && (
              <div className="mt-3 rounded-lg p-3 border" style={{ background: "#FFF0F8", borderColor: "#FFAED8" }} data-testid="admin-reset-link-panel">
                <div className="uppercase-label" style={{ color: BRAND.pink, fontSize: "0.6rem" }}>Reset link for {resetLink.referrer_name} · valid 24h</div>
                <div className="flex items-center gap-2 mt-1.5">
                  <input readOnly value={resetLink.reset_url} onFocus={(e) => e.target.select()}
                    className="flex-1 text-xs tabular bg-white rounded px-2 py-1.5 border" style={{ borderColor: "#FFAED8" }} data-testid="admin-reset-link-input" />
                  <Button size="sm" onClick={copyLink} className="btn-brand rounded-full gap-1 h-8 px-3" data-testid="admin-reset-link-copy">
                    {copied ? <Check size={13} /> : <Copy size={13} />} {copied ? "Copied" : "Copy"}
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  <Button size="sm" onClick={openWhatsApp} className="rounded-full gap-1 h-8 px-3 font-bold text-white" style={{ background: "#25D366" }} data-testid="admin-reset-whatsapp">
                    <MessageCircle size={13} /> Open in WhatsApp
                  </Button>
                  <Button size="sm" variant="ghost" onClick={copyMessage} className="rounded-full gap-1 h-8 px-3" data-testid="admin-reset-copy-message">
                    <Copy size={13} /> Copy full message
                  </Button>
                </div>
                <div className="text-xs mt-2" style={{ color: "#7A6D5F" }}>"Open in WhatsApp" launches your WhatsApp with a pre-filled friendly note — just pick {resetLink.referrer_name} and hit send.</div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function AdminDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [referrals, setReferrals] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [editing, setEditing] = useState(null);
  const [removing, setRemoving] = useState(null);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("active"); // "active" | "Referred" | "Confirmed Client" | "Sale Completed" | "deleted"

  const load = async ({ silent = false } = {}) => {
    if (silent) setRefreshing(true); else setLoading(true);
    try {
      const [r, s] = await Promise.all([api.get("/admin/referrals"), api.get("/admin/stats")]);
      // Detect newly-arrived referrals for a friendly toast
      const prevIds = new Set(referrals.map((x) => x.id));
      const fresh = r.data.filter((x) => !prevIds.has(x.id));
      setReferrals(r.data); setStats(s.data); setLastUpdated(new Date());
      if (silent && prevIds.size > 0 && fresh.length > 0) {
        toast.success(`${fresh.length} new ${fresh.length === 1 ? "referral" : "referrals"} arrived`);
      }
    } catch {
      if (!silent) toast.error("Could not load admin dashboard");
    } finally { setLoading(false); setRefreshing(false); }
  };

  useEffect(() => { load(); }, []); // initial

  // Auto-refresh: window focus + visibility change + 60s polling
  useEffect(() => {
    const silentReload = () => load({ silent: true });
    const onVisibility = () => { if (document.visibilityState === "visible") silentReload(); };
    window.addEventListener("focus", silentReload);
    document.addEventListener("visibilitychange", onVisibility);
    const interval = setInterval(silentReload, 60000);
    return () => {
      window.removeEventListener("focus", silentReload);
      document.removeEventListener("visibilitychange", onVisibility);
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referrals]);

  useEffect(() => {
    if (user === false) navigate("/login");
    if (user && user.role === "referrer") navigate("/dashboard");
  }, [user, navigate]);

  const filtered = useMemo(() => {
    return referrals.filter((r) => {
      // Status filter — matches the 5-option dropdown
      if (statusFilter === "active") {
        // Default: only Referred + Confirmed Client, and NOT removed
        if (r.removed) return false;
        if (r.status !== "Referred" && r.status !== "Confirmed Client") return false;
      } else if (statusFilter === "deleted") {
        if (!r.removed) return false;
      } else {
        // A specific stage was picked
        if (r.removed) return false;
        if (r.status !== statusFilter) return false;
      }
      if (!q.trim()) return true;
      const needle = q.toLowerCase();
      return (r.property_address || "").toLowerCase().includes(needle) ||
        (r.client_name || "").toLowerCase().includes(needle) ||
        (r.referrer_name || "").toLowerCase().includes(needle) ||
        (r.referrer_code || "").toLowerCase().includes(needle);
    });
  }, [referrals, q, statusFilter]);

  return (
    <AppShell>
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
        <div>
          <p className="uppercase-label" style={{ color: BRAND.pink }}>Admin · Pipeline</p>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl mt-1 leading-[1.02]" style={{ fontWeight: 700 }}>
            The <span className="italic">whole</span> network.
          </h1>
          <p className="text-sm md:text-base mt-3 max-w-xl" style={{ color: "#7A6D5F" }}>
            Every lead across every referrer — move deals through the stages and set the commission. The 5% fee calculates itself.
          </p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <Button
            onClick={() => load({ silent: true })}
            disabled={refreshing || loading}
            variant="ghost"
            className="rounded-full gap-2 h-10 px-4"
            data-testid="admin-refresh-button"
          >
            <RefreshCw size={14} className={refreshing ? "animate-spin" : ""} />
            {refreshing ? "Refreshing…" : "Refresh"}
          </Button>
          {lastUpdated && (
            <span className="text-[11px] tabular" style={{ color: "#7A6D5F" }} data-testid="admin-last-updated">
              Last updated {lastUpdated.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", second: "2-digit" })} · auto-refreshes every 60s
            </span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        <KpiCard label="Total referrals" value={stats?.total_referrals ?? "—"} testId="admin-kpi-total" />
        <KpiCard label="Active referrers" value={stats?.referrer_count ?? "—"} sub="Network size" testId="admin-kpi-referrers" />
        <KpiCard label="Gross commission" value={formatGBP(stats?.total_commission ?? 0)} sub="Across all deals" testId="admin-kpi-commission" />
        <KpiCard label="Referrer payouts" value={formatGBP(stats?.total_referrer_fees ?? 0)} sub={`${stats?.paid_count ?? 0} marked paid`} testId="admin-kpi-payouts" />
      </div>

      <div className="flex flex-col md:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "#9CA396" }} />
          <Input
            placeholder="Search by address, client, referrer or code"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9 h-11 rounded-lg bg-white"
            data-testid="admin-search-input"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="md:w-56 h-11" data-testid="admin-filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="active" data-testid="admin-filter-active">Active (default)</SelectItem>
            <SelectItem value="Referred" data-testid="admin-filter-referred">Referred</SelectItem>
            <SelectItem value="Confirmed Client" data-testid="admin-filter-confirmed">Confirmed Client</SelectItem>
            <SelectItem value="Sale Completed" data-testid="admin-filter-completed">Sale Completed</SelectItem>
            <SelectItem value="deleted" data-testid="admin-filter-deleted">Deleted</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="py-20 flex justify-center"><Loader2 className="animate-spin" size={24} style={{ color: BRAND.pink }} /></div>
      ) : (
        <div className="overflow-x-auto bg-white rounded-xl border border-border/60">
          <table className="w-full" data-testid="admin-referrals-table">
            <thead>
              <tr className="text-left border-b border-border/60">
                <th className="uppercase-label p-4">Address</th>
                <th className="uppercase-label p-4">Referrer</th>
                <th className="uppercase-label p-4">Stage</th>
                <th className="uppercase-label p-4 text-right">Commission</th>
                <th className="uppercase-label p-4 text-right">Referrer 5%</th>
                <th className="uppercase-label p-4 text-right"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} className={`border-b border-border/40 transition-colors ${r.removed ? "opacity-60" : "hover:bg-muted/40"}`} data-testid={`admin-row-${r.id}`}>
                  <td className="p-4">
                    <div className="font-semibold flex items-center gap-2">
                      {r.property_address || <span className="italic" style={{ color: "#9CA396" }}>No address</span>}
                      {r.removed && <StatusBadge status="Removed" />}
                    </div>
                    {r.client_name && (
                      <div className="text-xs mt-0.5" style={{ color: "#5C6B57" }}>Client: {r.client_name}</div>
                    )}
                    {r.removed && r.removed_reason && (
                      <div className="text-xs mt-1 italic" style={{ color: "#B91C1C" }}>Reason: {r.removed_reason}</div>
                    )}
                  </td>
                  <td className="p-4">
                    <div className="text-sm font-medium">{r.referrer_name}</div>
                    <div className="text-xs" style={{ color: "#5C6B57" }}>{r.referrer_email}</div>
                    {r.referrer_code && <div className="text-[10px] tabular mt-0.5" style={{ color: BRAND.pink }}>{r.referrer_code}</div>}
                  </td>
                  <td className="p-4">{r.removed ? <StatusBadge status="Removed" /> : <StatusBadge status={r.status} />}</td>
                  <td className="p-4 text-right tabular">{r.commission ? formatGBP(r.commission) : <span style={{ color: "#9CA396" }}>—</span>}</td>
                  <td className="p-4 text-right tabular font-bold" style={{ color: BRAND.pink }}>{formatGBP(r.referral_value)}</td>
                  <td className="p-4 text-right whitespace-nowrap">
                    {!r.removed && (
                      <>
                        <Button size="sm" variant="ghost" onClick={() => setEditing(r)} className="rounded-full gap-1" data-testid={`admin-edit-${r.id}`}>
                          <Edit3 size={14} /> Edit
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setRemoving(r)} className="rounded-full gap-1" style={{ color: "#B91C1C" }} data-testid={`admin-remove-${r.id}`}>
                          <Trash2 size={14} /> Remove
                        </Button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan="6" className="p-12 text-center" style={{ color: "#5C6B57" }} data-testid="admin-empty">
                  No referrals match your filters.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <EditReferralDialog
        referral={editing}
        open={!!editing}
        onClose={() => setEditing(null)}
        onSaved={(updated) => {
          setReferrals((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
          load();
        }}
      />
      <RemoveDialog
        referral={removing}
        open={!!removing}
        onClose={() => setRemoving(null)}
        onRemoved={(updated) => {
          setReferrals((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
          load();
        }}
      />
    </AppShell>
  );
}
